import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Shield, FlaskConical, Scale, BookOpen, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import LatticeCanvas from "@/components/LatticeCanvas";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine } from "@/components/Reveal";

const CHAPTERS = [
  { n: "01", title: "Capital Governance", text: "Every entity, every account, every dollar operates under an explicit mandate. Capital is allocated by policy, not impulse — and every allocation leaves a traceable rationale." },
  { n: "02", title: "Research", text: "Hussar Lab is the research engine behind Treasury. It studies behavior, benchmarks outcomes, and converts operational history into institutional knowledge." },
  { n: "03", title: "Risk Controls", text: "Exposure limits, drawdown discipline, and pre-committed exit rules are enforced by the system — not remembered by the operator." },
  { n: "04", title: "Execution Discipline", text: "Execution follows checklists, reconciliation, and sign-off. Speed is never celebrated at the expense of correctness." },
  { n: "05", title: "Continuous Learning", text: "Every week closes with a Learning Report. What worked, what failed, what changes. The sanitized findings become the public Weekly Intelligence record." },
];

const BENTO = [
  { icon: Scale, title: "Multi-Entity Accounting", text: "Personal, business, and family capital tracked as one governed system with clean entity boundaries.", span: "md:col-span-7" },
  { icon: Shield, title: "Immutable Audit Trail", text: "Every decision is versioned and preserved. History is never silently rewritten.", span: "md:col-span-5" },
  { icon: FlaskConical, title: "Hussar Lab", text: "The internal research engine: backtesting, benchmarking, and weekly learning reports.", span: "md:col-span-5" },
  { icon: RefreshCw, title: "Reconciliation & Benchmarking", text: "Provider connections reconciled against policy benchmarks — continuously.", span: "md:col-span-7" },
];

function FeaturedIntelligence() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data.filter((a) => a.status === "PUBLISHED").slice(0, 3))).catch(() => setArticles([]));
  }, []);

  return (
    <section data-testid="featured-intelligence" className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto py-24 sm:py-32">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <div className="metadata-label mb-4">The Public Record</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-hussar-ink tracking-tight">Weekly Intelligence</h2>
          </div>
          <Link to="/intelligence" data-testid="view-archive-link" className="btn-ghost w-fit">
            View the Archive <ArrowUpRight size={14} />
          </Link>
        </div>
      </Reveal>

      {articles === null ? (
        <div className="h-40 flex items-center justify-center text-hussar-muted font-mono text-xs uppercase tracking-widest">Loading record…</div>
      ) : articles.length === 0 ? (
        <Reveal>
          <div className="border border-hussar-gold/20 bg-hussar-card p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-hussar-gold/60" />
            <div className="metadata-label mb-4">Edition No. 001 — In Preparation</div>
            <p className="font-serif text-xl sm:text-2xl text-hussar-ink leading-relaxed max-w-3xl">
              The first Weekly Intelligence edition is moving through the publishing pipeline: private Learning Report,
              sanitization, owner approval. It will appear here as a permanent public record.
            </p>
            <Link to="/lab" data-testid="pipeline-link" className="inline-flex items-center gap-2 mt-8 text-hussar-gold font-mono text-xs uppercase tracking-[0.2em] hover:text-hussar-goldbright transition-colors">
              How the pipeline works <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.12}>
              <Link
                to={`/intelligence/${a.slug}`}
                data-testid={`featured-article-${a.slug}`}
                className="group block border border-white/5 bg-hussar-card p-8 h-full transition-colors duration-300 hover:border-hussar-gold/40"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-gold">{a.confidence_label} confidence</span>
                  <span className="font-mono text-[10px] text-hussar-muted">v{a.version}</span>
                </div>
                <h3 className="font-serif text-xl text-hussar-ink leading-snug group-hover:text-hussar-goldbright transition-colors duration-300">{a.title}</h3>
                <p className="text-hussar-slate text-sm font-light leading-relaxed mt-4 line-clamp-3">{a.dek}</p>
                <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-hussar-muted">
                  Evidence cutoff · {a.evidence_cutoff || "—"}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "Hussar Treasury — Every Dollar Has a Job";
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", window.location.origin + "/");
  }, []);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.2]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <LatticeCanvas />
        <div className="absolute inset-0 bg-gradient-to-b from-hussar-bg/30 via-transparent to-hussar-bg" />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="metadata-label mb-8"
            data-testid="hero-eyebrow"
          >
            Hussar Treasury · Institutional Research Division
          </motion.div>
          <h1 className="font-serif tracking-tight leading-[0.95] text-hussar-ink text-[13vw] sm:text-[9vw] lg:text-[7.5vw]">
            <MaskedLine delay={0.35}>Every Dollar</MaskedLine>
            <MaskedLine delay={0.5}>
              Has a <span className="gold-text-shimmer animate-gold-shimmer">Job.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-2xl text-hussar-slate text-base sm:text-lg font-light leading-relaxed"
            data-testid="hero-positioning"
          >
            Hussar Treasury is a multi-entity treasury operating system combining capital governance, research,
            risk controls, execution discipline, accounting, benchmarking and continuous learning.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/request-access" data-testid="hero-request-access-btn" className="btn-gold">
              Request Early Access <ArrowRight size={14} />
            </Link>
            <Link to="/lab" data-testid="hero-explore-lab-btn" className="btn-ghost">
              Explore Hussar Lab
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.35em] text-hussar-muted"
        >
          Scroll — The Manifesto
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto-section" className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto py-24 sm:py-36">
        <Reveal>
          <div className="metadata-label mb-4">The Operating Manifesto</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-hussar-ink tracking-tight max-w-3xl leading-tight mb-20">
            Five disciplines. One system. Zero improvisation.
          </h2>
        </Reveal>
        <div className="flex flex-col">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <div
                data-testid={`manifesto-chapter-${c.n}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-12 border-t border-white/5 transition-colors duration-500 hover:border-hussar-gold/30"
              >
                <div className="md:col-span-2 font-mono text-hussar-gold/50 text-sm tracking-[0.3em] group-hover:text-hussar-gold transition-colors duration-500">
                  {c.n} —
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-serif text-2xl sm:text-3xl text-hussar-ink group-hover:text-hussar-goldbright transition-colors duration-500">{c.title}</h3>
                </div>
                <div className="md:col-span-6">
                  <p className="text-hussar-slate font-light leading-relaxed">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SYSTEM BENTO */}
      <section data-testid="system-bento" className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-24 sm:pb-36">
        <Reveal>
          <div className="metadata-label mb-4">The System</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-hussar-ink tracking-tight mb-16">
            Built like an institution, not an app.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {BENTO.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} className={b.span}>
              <div className="group border border-white/5 bg-hussar-card p-10 h-full transition-all duration-500 hover:border-hussar-gold/40 hover:bg-hussar-bg2">
                <b.icon className="text-hussar-gold mb-8" size={28} strokeWidth={1.25} />
                <h3 className="font-serif text-xl sm:text-2xl text-hussar-ink mb-4">{b.title}</h3>
                <p className="text-hussar-slate text-sm font-light leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-hussar-gold/10 bg-hussar-bg2/40">
        <div className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto py-20 grid grid-cols-1 sm:grid-cols-3 gap-12">
          {[
            { v: "05", l: "Governance disciplines, enforced by system" },
            { v: "01", l: "Permanent public research record" },
            { v: "00", l: "Silent rewrites. Ever." },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 0.1}>
              <div data-testid={`stat-${i}`} className="text-center sm:text-left">
                <div className="font-serif text-6xl sm:text-7xl gold-text-shimmer animate-gold-shimmer">{s.v}</div>
                <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-hussar-muted">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FeaturedIntelligence />

      {/* HUSSAR LAB TEASER */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-24 sm:pb-36">
        <Reveal>
          <div className="relative border border-hussar-gold/20 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Abstract financial research network"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-hussar-bg via-hussar-bg/80 to-transparent" />
            <div className="relative p-10 sm:p-16 lg:p-20 max-w-2xl">
              <div className="metadata-label mb-4">The Research Engine</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink tracking-tight leading-tight mb-6">
                Hussar Lab turns operational history into institutional knowledge.
              </h2>
              <p className="text-hussar-slate font-light leading-relaxed mb-10">
                Every week the private treasury system produces a Learning Report. Hussar Lab sanitizes it,
                the owner approves it, and the findings cross the boundary as a permanent public edition of
                Weekly Intelligence — evidence-dated and confidence-labelled.
              </p>
              <Link to="/lab" data-testid="lab-teaser-btn" className="btn-ghost">
                Read the Methodology <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* REQUEST ACCESS TEASER */}
      <section data-testid="access-teaser" className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-28 sm:pb-40">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <div className="metadata-label mb-6">Limited Cohorts</div>
            <h2 className="font-serif text-3xl sm:text-5xl text-hussar-ink tracking-tight leading-tight mb-8">
              Access is reviewed personally. It is not open to the public.
            </h2>
            <p className="text-hussar-slate font-light leading-relaxed mb-12">
              Hussar Treasury is being opened to a small number of individuals, families, and businesses.
              If you believe your capital deserves discipline, request a place.
            </p>
            <Link to="/request-access" data-testid="teaser-request-access-btn" className="btn-gold">
              Request Early Access <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
